import { faArrowCircleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Container } from '@material-ui/core';
import { Filter, SGSCard, SGSColumnFilter, SGSTableCtrl, SGSTooltip } from 'components';
import { useAppContext } from 'core/contexts';
import { getWorkStoppageHistory } from 'core/services/workStoppage';
import { useQueryConf } from 'core/utils/useQuery';
import { optionTypeAttestation } from 'features/arretTravail/models/typeAttestation';
import { optionReasonForWorkStop } from 'features/arretTravail/models/workStopReason';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux'; // Import useDispatch and useSelector
import { useHistory } from 'react-router';
import { Cell, ColumnInterface } from 'react-table';
import { filtersChanged, pageChanged, pageSizeChanged, resetSearchParams, sortByChanged } from '../../redux/searchParamsSliceHistory';
import styles from './history.module.scss';

const commonColumnProps = {
    headerClassName: styles.tableBorderRight__header,
    className: `${styles.tableBorderRight__cell} text-left`,
    disableFilters: true,
};

const ArretTravailHistory = () => {
    const { t } = useTranslation();
    const { user } = useAppContext();
    const history = useHistory();
    const dispatch = useDispatch(); // Initialize dispatch
    const searchParams = useSelector((state: any) => state.searchParamsHistory?.value || JSON.stringify({ page: 1, pageSize: 10, sortBy: [], search: [] })); // Ensure searchParams is always initialized
    const [activeFilters, setActiveFilters] = useState<Filter[]>(JSON.parse(searchParams).search ?? []);

    type History = {
        traitement: string;
        subscriber: string;
        attType: string;
        reason: string;
        fullname: string;
        registrationNumber: string;
        ssnumber: string;
        status: string;
        lastDayWorked: string;
        start: string;
        end: string;
        returnToWork: string;
        returnToWorkDate: string;
        date: string;
        hour: string;
    };

    const methods = useForm({
        defaultValues: {},
    });

    const filterableColumnLabels = {
        // Subscriber-related fields
        subscriber_social_reason: {
            value: t('Souscripteur'),
            type: 'input', // Allows for text input search
        },

        // Affilie-related fields
        affilie_lastname: {
            value: t("Nom de l'assuré"),
            type: 'input', // Allows text input for partial match
        },
        affilie_firstname: {
            value: t("Prénom de l'assuré"),
            type: 'input', // Allows text input for partial match
        },
        affilie_registration_number: {
            value: t("Numéro d'immatriculation"),
            type: 'input', // Allows text input for partial match
        },

        // Work stoppage type
        att_type: {
            value: t("Type d'attestation"),
            type: 'autocomplete',
            options: optionTypeAttestation,
        },

        // Work stoppage status
        affilie_status: {
            value: t("Statut de l'assuré"),
            type: 'autocomplete',
            options: [
                { label: 'CADRE', value: 'C' },
                { label: 'NON CADRE', value: 'NC' },
            ], // These options should correspond to your system's status values
        },

        // Dates-related filters
        startDate: {
            value: t('Arrêt du'),
            type: 'date', // Date picker for the start date
        },
        endDate: {
            value: t('Arrêt au'),
            type: 'date', // Date picker for the end date
        },
        return_to_work_date: {
            value: t('Date de reprise'),
            type: 'date', // Date picker for the return-to-work date
        },

        // Work stoppage reason
        reason: {
            value: t('Motif'),
            type: 'autocomplete',
            options: optionReasonForWorkStop,
        },
    };

    /**
     * Handles the change of filter value.
     * Resets the page to 1 and sets the new filter.
     *
     * @param {string} value - The new filter value.
     */
    const handleChangeFilter = (value) => {
        dispatch(pageChanged(1)); // Reset page to 1 on filter change
        setActiveFilters(value); // Update local state with new filters
    };

    // Reset searchParams on component mount, unless 'noreset' flag is in URL
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const keepNav = queryParams.get('noreset');
        if (!keepNav) {
            dispatch(resetSearchParams()); // Reset searchParams in Redux
            setActiveFilters([]); // Clear local filters
        }
    }, [dispatch]);

    const columns = useMemo<ColumnInterface<History>[]>(
        () => [
            {
                id: 'Traitement',
                Header: t('Trait.'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center`,
                disableFilters: true,
                accessor: 'traitement',
                minWidth: 80,
                maxWidth: 80,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{row.traitement}</Box>,
            },
            {
                id: 'subscriber',
                Header: t('Souscripteur'),
                ...commonColumnProps,
                accessor: 'subscriber',
                minWidth: 250,
                maxWidth: 200,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{row.subscriber}</Box>,
            },
            {
                id: 'attType',
                Header: t('Attestation Type'),
                ...commonColumnProps,
                accessor: 'attType',
                minWidth: 100,
                maxWidth: 150,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{row.attType}</Box>,
            },
            {
                id: 'fullname',
                Header: t('Assuré'),
                ...commonColumnProps,
                accessor: 'fullname',
                minWidth: 150,
                maxWidth: 300,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{row.fullname}</Box>,
            },
            {
                id: 'reason',
                Header: t('Motif'),
                ...commonColumnProps,
                accessor: 'reason',
                minWidth: 200,
                maxWidth: 250,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{row.reason}</Box>,
            },
            {
                id: 'lastDayWorked',
                Header: t('Dernier jour travaillé'),
                ...commonColumnProps,
                accessor: 'lastDayWorked',
                minWidth: 150,
                maxWidth: 250,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{moment(row.lastDayWorked).format('DD/MM/YYYY')}</Box>,
            },
            {
                id: 'start',
                Header: t('Arrêt Prescrit Du'),
                ...commonColumnProps,
                accessor: 'start',
                minWidth: 150,
                maxWidth: 250,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{moment(row.start).format('DD/MM/YYYY')}</Box>,
            },
            {
                id: 'end',
                Header: t('Arrêt Prescrit Au'),
                ...commonColumnProps,
                accessor: 'end',
                minWidth: 150,
                maxWidth: 250,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{moment(row.end).format('DD/MM/YYYY')}</Box>,
            },
            {
                id: 'returnToWork',
                Header: t('Reprise'),
                ...commonColumnProps,
                accessor: 'returnToWork',
                minWidth: 150,
                maxWidth: 250,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{row.returnToWork}</Box>,
            },
            {
                id: 'returnToWorkDate',
                Header: t('Date De Reprise'),
                ...commonColumnProps,
                accessor: 'returnToWorkDate',
                minWidth: 150,
                maxWidth: 250,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{row.returnToWorkDate ? moment(row.returnToWorkDate).format('DD/MM/YYYY') : ''}</Box>,
            },
            {
                id: 'hour',
                Header: t('Heure'),
                ...commonColumnProps,
                accessor: 'hour',
                minWidth: 150,
                maxWidth: 250,
                Cell: ({ row: { original: row } }: Cell<History>) => <Box>{moment(row.hour, 'HH:mm:ss').format('H:mm')}</Box>,
            },
            {
                Header: '',
                accessor: 'none' as any,
                className: 'p-0 align-items-stretch',
                disableFilters: true,
                disableSortBy: true,
                minWidth: 25,
                maxWidth: 25,
                Cell: ({ row }: Cell<History>) => (
                    <div className={styles.actions}>
                        <SGSTooltip title={<Box>{t('details')}</Box>}>
                            <div className={styles.actions__menu} onClick={() => handleDetails(row)}>
                                <FontAwesomeIcon icon={faArrowCircleRight} size="lg" />
                            </div>
                        </SGSTooltip>
                    </div>
                ),
            },
        ],
        [t],
    );

    const handleDetails = (row: any) => {
        console.log('Details for row:', row.original);
        history.push('/arrets/declare?id=' + row.original.id);
    };

    const {
        isLoading,
        isFetching,
        data: dataHistories,
    } = useQuery(
        [user.login, 'histories', searchParams],
        async () => {
            const parsedParams = JSON.parse(searchParams); // Ensure we are safely parsing the searchParams
            const { page, pageSize, sortBy, search } = parsedParams;
            const sortPayload = sortBy.length ? createSortPayload(sortBy[0].id, sortBy[0].desc) : [];
            return await getWorkStoppageHistory(page, pageSize, sortPayload, search);
        },
        { ...useQueryConf, cacheTime: 0 },
    );

    const isLoadingData = isLoading || isFetching;

    const historyAT = dataHistories?.data ?? [];
    const totalPages = dataHistories?.totalPage ?? 0;
    const totalResults = dataHistories?.totalResultat ?? 0;

    return (
        <Container maxWidth="xl">
            <Box display="flex" className="my-2" justifyContent="left" alignItems="center">
                <FormProvider {...methods}>
                    <SGSColumnFilter columns={filterableColumnLabels} className="mr-5" filters={activeFilters} onFiltersChange={handleChangeFilter} customLabel={' '} />
                </FormProvider>
            </Box>
            <SGSCard>
                <SGSTableCtrl
                    onRowClick={handleDetails}
                    columns={columns}
                    data={historyAT}
                    loading={isLoadingData}
                    pageCount={totalPages}
                    globalFilter={activeFilters}
                    totalResults={totalResults}
                    filtersChanged={filtersChanged}
                    pageChanged={pageChanged}
                    pageSizeChanged={pageSizeChanged}
                    sortByChanged={sortByChanged}
                    searchParams={useSelector((state: any) => state.searchParamsHistory.value)}
                />
            </SGSCard>
        </Container>
    );
};

export default ArretTravailHistory;

const createSortPayload = (column: string, order: boolean): string => {
    const payload = {
        column,
        order: order ? 'ASC' : 'DESC',
    };
    return JSON.stringify(payload);
};
